/**
 * One connection, opened from its card on the Connected tab.
 *
 * The card answers "is this working"; this page answers "what exactly is it, and what has
 * happened to it". The list endpoint already returns every field this page shows, so the page
 * reads the list and selects one row rather than asking for a resource that does not exist.
 *
 * Nothing here shows a credential: the platform stores a reference and never returns a value,
 * which is the whole point of the sealed-credential design.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router";
import { PageHeader } from "@/components/bespoke";
import { ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, connectionActivityQuery, connectionsQuery } from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { describeActivityAction, formatActivityTime } from "@/pages/connection-activity";
import type { Connection } from "@zworks/console-contracts";

const healthLabels: Record<string, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  unreachable: "Unreachable",
  unknown: "Unknown",
};

const statusLabels: Record<string, string> = {
  pending: "Pending",
  active: "Active",
  degraded: "Degraded",
  failing: "Failing",
  expired: "Expired",
  revoked: "Revoked",
  blocked: "Blocked",
};

/** A date the page can render, or a dash when the field is null. */
function when(value: string | null): string {
  return value === null ? "Never" : new Date(value).toLocaleString();
}

/** The scopes a connection was granted, or the fact that it has none. */
function scopeList(connection: Connection): string {
  return connection.scopes.length > 0 ? connection.scopes.join(", ") : "No scopes granted";
}

function DetailRow({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b py-3 last:border-b-0 sm:flex-row sm:gap-4">
      <dt className="w-44 shrink-0 text-sm text-muted-foreground">{term}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function ConnectionActivity({
  projectId,
  connection,
}: {
  projectId: string;
  connection: Connection;
}) {
  const activity = useInfiniteQuery(connectionActivityQuery(projectId, connection.id));
  const entries = activity.data?.pages.flatMap((page) => page.activity) ?? [];

  if (activity.isPending) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Loading activity…
      </p>
    );
  }
  if (activity.isError) {
    return (
      <p className="text-sm text-muted-foreground" role="alert">
        {activity.error instanceof ApiError
          ? (activity.error.problem?.detail ?? activity.error.message)
          : "Activity could not be loaded."}
      </p>
    );
  }
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Nothing has happened to this connection yet.</p>
    );
  }
  return (
    <ol className="divide-y" aria-label={`Activity for ${connection.name}`}>
      {entries.map((entry) => (
        <li key={entry.id} className="flex flex-col gap-1 py-2 first:pt-0 last:pb-0">
          <span className="text-sm font-medium">{describeActivityAction(entry.action)}</span>
          <span className="text-xs text-muted-foreground">
            {entry.actorKind === "system" ? "System" : "Member"} ·{" "}
            <time dateTime={entry.occurredAt}>{formatActivityTime(entry.occurredAt)}</time>
          </span>
        </li>
      ))}
    </ol>
  );
}

export function ConnectionDetailPage() {
  const { connectionId } = useParams();
  const { project, loaded } = useCurrentProject();
  const connections = useQuery({
    ...connectionsQuery(project?.id ?? ""),
    enabled: loaded && !!project,
  });

  const backLink = (
    <Link
      to="/integrations"
      className="inline-flex items-center gap-1 text-sm underline-offset-4 hover:underline"
    >
      <ArrowLeft aria-hidden className="size-4" />
      Back to integrations
    </Link>
  );

  if (!loaded || connections.isPending) {
    return (
      <>
        <PageHeader title="Connection" />
        {backLink}
        <LoadingState label="Loading the connection…" />
      </>
    );
  }
  if (connections.isError) {
    const error = connections.error;
    if (error instanceof ApiError && error.status === 403) {
      return (
        <>
          <PageHeader title="Connection" />
          {backLink}
          <ForbiddenState
            title="Not available"
            description="You cannot read this project's connections."
          />
        </>
      );
    }
    return (
      <>
        <PageHeader title="Connection" />
        {backLink}
        <ErrorState
          title="The connection could not be loaded"
          requestId=""
          description={
            error instanceof ApiError
              ? (error.problem?.detail ?? error.message)
              : "The connection could not be loaded."
          }
          onRetry={() => void connections.refetch()}
        />
      </>
    );
  }

  const connection = (connections.data ?? []).find((row) => row.id === connectionId);
  if (!connection) {
    // A route for a row the list does not contain is a stale link, not an empty page.
    return (
      <>
        <PageHeader title="Connection" />
        {backLink}
        <p className="text-sm text-muted-foreground">
          This connection no longer exists in this project. It may have been released.
        </p>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={connection.name} />
      {backLink}

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            {connection.name}
            <Badge variant="secondary">
              {statusLabels[connection.status] ?? connection.status}
            </Badge>
            {connection.granted ? (
              <Badge variant="secondary" title="Shared with this project by its organisation">
                Organisation connection
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription className="capitalize">{connection.integrationType}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl>
            <DetailRow term="Health">
              {healthLabels[connection.health] ?? connection.health}
            </DetailRow>
            <DetailRow term="Last health check">{when(connection.healthCheckedAt)}</DetailRow>
            <DetailRow term="Auth method">{connection.authMethod}</DetailRow>
            <DetailRow term="Scopes">{scopeList(connection)}</DetailRow>
            <DetailRow term="Credential">
              {connection.credentialRef === null ? (
                "No credential recorded"
              ) : (
                // The reference, never a value: the platform stores a pointer and returns that.
                <code className="text-xs">{connection.credentialRef}</code>
              )}
            </DetailRow>
            <DetailRow term="Expires">{when(connection.expiresAt)}</DetailRow>
            <DetailRow term="Last used">{when(connection.lastUsedAt)}</DetailRow>
            <DetailRow term="Owner">
              {connection.scope.level === "organisation" ? "Organisation" : "This project"}
            </DetailRow>
            <DetailRow term="Created">{when(connection.createdAt)}</DetailRow>
            <DetailRow term="Updated">{when(connection.updatedAt)}</DetailRow>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
          <CardDescription>What has happened to this connection, oldest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {project ? <ConnectionActivity projectId={project.id} connection={connection} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
